import { useEffect } from 'react';
import { ButtonHeadless, Modal, Spin } from '@wajeez-web/components/ui';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  FormTextInput,
  FormTextInputV2,
} from '@wajeez-web/components/form-builder';
import { Server } from '@wajeez/wajeez.microservices';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { BiLoaderAlt } from 'react-icons/bi';
import {
  clearContentEntityListById,
  createContentEntityList,
  editContentEntityList,
  getContentEntityListById,
} from '@wajeez-web/stores/content';
import {
  useContentEntityListById,
  useCreateContentEntityList,
  useEditContentEntityList,
} from '@wajeez-web/components/helpers';

type Input = Server.EditContentEntityListInput;

interface ContentEntityListModalProps {
  isOpen: boolean;
  onClose: () => void;
  id?: number;
  onSubmit?: (id?: number) => void | Promise<void>;
}

export const ContentEntityListModal = ({
  isOpen,
  onClose,
  id,
  onSubmit,
}: ContentEntityListModalProps) => {
  const { status: createContentEntityListStatus } =
    useCreateContentEntityList();
  const { status: editContentEntityListStatus } = useEditContentEntityList();
  const contentEntityListById = useContentEntityListById();

  const { handleSubmit, control, reset } = useForm<Input>();

  const onFormSubmit: SubmitHandler<Input> = async (data) => {
    if (!id) {
      createContentEntityList(data, onSubmit);
    } else {
      editContentEntityList({ ...data, id }, onSubmit);
    }
  };

  useEffect(() => {
    if (id) {
      getContentEntityListById(id);
    }

    return () => {
      clearContentEntityListById();
    };
  }, [id]);

  useEffect(() => {
    if (contentEntityListById.data) {
      reset(contentEntityListById.data);
    }
  }, [contentEntityListById.data]);

  const isLoading = contentEntityListById.status === 'loading';

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="Content Entity List"
      alertTarget="modal"
      fontClassName="font-[Tajawal]"
    >
      <div className="w-full">
        {isLoading ? (
          <div className="w-full min-h-[200px] flex justify-center items-center">
            <Spin />
          </div>
        ) : (
          <form
            onSubmit={handleSubmit(onFormSubmit)}
            noValidate
            className="w-full flex flex-col justify-start items-start"
          >
            <div className="w-[300px] flex flex-col justify-start items-start space-y-2">
              <FormTextInputV2
                name="referenceName"
                control={control}
                fieldMeta={{
                  fieldType: Server.FieldType.Text,
                  fieldOptions: {},
                  label: 'Reference Name',
                  placeHolder: 'Reference Name',
                  required: true,
                }}
              />
            </div>
            <div className="mt-6 w-full flex justify-end items-center space-s-2">
              <div className="w-[70px]">
                <ButtonHeadless
                  bgStyle="bg-warmGray-300"
                  borderStyle="border-none"
                  paddingYStyle="py-1"
                  action={onClose}
                >
                  <div className="text-warmGray-600 text-[16px] font-medium">
                    <ReturnTranslatedText
                      text="buttons.Cancel"
                      origintext="Cancel"
                    />
                  </div>
                </ButtonHeadless>
              </div>
              <div className="w-[70px]">
                <ButtonHeadless
                  type="submit"
                  borderStyle="border-none"
                  paddingYStyle="py-1"
                >
                  <div className="text-white text-[16px] font-medium flex justify-center items-center space-s-2">
                    <ReturnTranslatedText
                      text="buttons.Save"
                      origintext="Save"
                    />
                    {(createContentEntityListStatus === 'loading' ||
                      editContentEntityListStatus === 'loading') && (
                      <BiLoaderAlt className="animate-spin w-5" />
                    )}
                  </div>
                </ButtonHeadless>
              </div>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};
